import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  registerDeviceToken,
  subscribeToForegroundMessages,
} from "services/FirebaseService";
import { Notification, toast } from "components/ui";
import { handleNotificationPayload } from "utils/notificationSound";
import { fetchMenuCounts } from "store/menuCount/menuCountSlice";

const useFirebaseMessaging = (enabled) => {
  const dispatch = useDispatch();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      initializedRef.current = false;
      return undefined;
    }

    if (initializedRef.current) {
      return undefined;
    }

    initializedRef.current = true;
    let unsubscribe = () => {};

    const refreshMenuCounts = () => {
      dispatch(fetchMenuCounts());
    };

    const showNotificationToast = async (payload) => {
      const { title, body } = await handleNotificationPayload(payload);
      refreshMenuCounts();

      toast.push(
        <Notification type="info" title={title} closable duration={5000}>
          {body}
        </Notification>,
      );
    };

    const onServiceWorkerMessage = (event) => {
      if (event.data?.type === "PLAY_NOTIFICATION_SOUND") {
        showNotificationToast(event.data.payload || {});
      }
    };

    const setupMessaging = async () => {
      try {
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.addEventListener(
            "message",
            onServiceWorkerMessage,
          );
        }

        await registerDeviceToken();
        unsubscribe = await subscribeToForegroundMessages(showNotificationToast);
      } catch (error) {
        console.error("Failed to initialize Firebase messaging:", error);
        initializedRef.current = false;
      }
    };

    setupMessaging();

    return () => {
      unsubscribe();
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          onServiceWorkerMessage,
        );
      }
    };
  }, [dispatch, enabled]);
};

export default useFirebaseMessaging;
